import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { AppShell } from '../components/Layout';
import { Banner, EmptyState, Loading } from '../components/ui';
import { formatDateTime } from '../lib/format';

/**
 * Development mailbox.
 *
 * Email and SMS are logged rather than transmitted in development, so signing
 * links and one-time codes are readable here. Disabled when running in
 * production.
 */
export function Mailbox() {
  const [messages, setMessages] = useState<any[] | null>(null);
  const [filter, setFilter] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await api.get(`/dev/mailbox${filter ? `?to=${encodeURIComponent(filter)}` : ''}`);
      setMessages(data.messages);
    } catch {
      setError('The development mailbox is disabled in this environment.');
    }
  }, [filter]);

  useEffect(() => {
    void load();
    const timer = setInterval(() => { void load(); }, 5000);
    return () => clearInterval(timer);
  }, [load]);

  const signingPath = (url?: string | null) => {
    if (!url) return null;
    try { return new URL(url).pathname; } catch { return null; }
  };

  return (
    <AppShell nav={[{ to: '/', label: 'Home' }, { to: '/app', label: 'Dashboard' }]}>
      <div className="shell section stack">
        <header className="stack-s">
          <h1 style={{ margin: 0 }}>Development mailbox</h1>
          <p className="muted small" style={{ margin: 0 }}>
            Outbound email and SMS are recorded here instead of being sent. Use it
            to pick up signing links and one-time codes while testing.
          </p>
        </header>

        {error ? <Banner tone="warn" title="Unavailable">{error}</Banner> : (
          <>
            <input className="input" type="search" placeholder="Filter by recipient address"
                   style={{ maxWidth: 360 }} value={filter}
                   onChange={(event) => setFilter(event.target.value)} />
            {!messages ? <Loading /> : messages.length === 0 ? (
              <EmptyState title="No messages yet">
                Send an agreement and its invitation will appear here.
              </EmptyState>
            ) : (
              <div className="stack">
                {messages.map((message) => {
                  const path = signingPath(message.action_url);
                  return (
                    <article className="mail" key={message.id}>
                      <div className="mail__head">
                        <div className="row row--between row--top">
                          <div style={{ minWidth: 0 }}>
                            <strong style={{ fontSize: '.94rem' }}>{message.subject}</strong>
                            <div className="small muted truncate">
                              {message.channel} to {message.to} · {message.kind}
                            </div>
                          </div>
                          <span className={`pill pill--${
                            message.status === 'delivered' ? 'ok' : 'danger'}`}>
                            {message.status}
                          </span>
                        </div>
                        {message.failure_reason && (
                          <p className="small" style={{ margin: '6px 0 0', color: 'var(--danger)' }}>
                            {message.failure_reason}
                          </p>
                        )}
                      </div>
                      <div className="mail__body">{message.body}</div>
                      {path && (
                        <div style={{ padding: '0 15px 14px' }}>
                          <Link className="btn btn--small btn--primary" to={path}>
                            Open link
                          </Link>
                        </div>
                      )}
                      <div className="mail__head" style={{ borderTop: '1px solid var(--line)',
                                                            borderBottom: 'none' }}>
                        <span className="small muted">{formatDateTime(message.created_at)}</span>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}
